// ui
import {
  Box,
  Alert,
  FormControl,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";

// ★cmd-po_1139: PoModalShell + PoButton で catalog 外殻を導入
import { PoModalShell } from "../common/PoModalShell";
import { PoButton } from "../common/PoButton";

export type CancellSmsItem = {
  id: number;
  cancell_sms_name: string;
  cancell_sms_type: number;
  cancell_sms_sender: string;
  cancell_sms_body: string;
  cancell_sms_title: string;
  source: "product" | "outside_entry_form";
};

interface DataCancelModalProps {
  onModal: boolean;
  swCloseModal: () => void;
  cancelDataInf: {
    status: null | "loading" | "open" | "success" | "failed";
    key: { [key: string]: string };
    message: string;
    cancellAction: string;
    cancellSmsList: CancellSmsItem[];
    selectedCancellSms: { id: number; source: "product" | "outside_entry_form" } | null;
    destinationPhoneNumber: string;
    destinationMailAddress: string;
  };
  setCancelDataInf: React.Dispatch<
    React.SetStateAction<{
      status: null | "loading" | "open" | "success" | "failed";
      key: { [key: string]: string };
      message: string;
      cancellAction: string;
      cancellSmsList: CancellSmsItem[];
      selectedCancellSms: { id: number; source: "product" | "outside_entry_form" } | null;
      destinationPhoneNumber: string;
      destinationMailAddress: string;
    }>
  >;
  cancelData: () => void;
}

const SEND_NONE_VALUE = "none";
// 区切りは可視文字にする: "\u0001" は UI反映part3 で消失し回帰を起こした(cmd-po_1263)
const VALUE_SEP = "|";

export function DataCancelModal(props: DataCancelModalProps) {
  const { onModal, swCloseModal, cancelDataInf, setCancelDataInf, cancelData } =
    props;

  const hasSmsList = cancelDataInf.cancellSmsList.length > 0;
  const selectedValue =
    cancelDataInf.selectedCancellSms === null
      ? SEND_NONE_VALUE
      : `${cancelDataInf.selectedCancellSms.id}${VALUE_SEP}${cancelDataInf.selectedCancellSms.source}`;
  const selectedItem = cancelDataInf.cancellSmsList.find(
    (m) =>
      cancelDataInf.selectedCancellSms &&
      m.id === cancelDataInf.selectedCancellSms.id &&
      m.source === cancelDataInf.selectedCancellSms.source
  );

  const footer =
    cancelDataInf.status === "open" ? (
      <>
        <PoButton
          variant="secondary"
          data-testid="data-cancel-close-button"
          onClick={swCloseModal}
        >
          閉じる
        </PoButton>
        <PoButton
          variant="primary"
          data-testid="data-cancel-execute-button"
          onClick={cancelData}
        >
          解約
          <CancelIcon fontSize="small" />
        </PoButton>
      </>
    ) : cancelDataInf.status === "success" ? (
      <PoButton
        variant="secondary"
        data-testid="data-cancel-success-close-button"
        onClick={swCloseModal}
      >
        閉じる
      </PoButton>
    ) : cancelDataInf.status === "failed" ? (
      <PoButton
        variant="secondary"
        data-testid="data-cancel-retry-button"
        onClick={() =>
          setCancelDataInf((prev) => ({ ...prev, status: "open", message: "" }))
        }
      >
        戻る
      </PoButton>
    ) : null;

  return (
    <PoModalShell
      open={onModal}
      title="解約"
      onClose={swCloseModal}
      testidPrefix="data-cancel"
      footer={footer}
    >
      {cancelDataInf.status === "open" ? (
        <>
          <Alert
            severity="error"
            sx={{
              bgcolor: "var(--sem-error-bg, #f6d5d1)",
              color: "var(--sem-error, #a53229)",
              fontSize: 12,
              borderRadius: "var(--radius-md, 10px)",
              p: "var(--sp-3, 12px) var(--sp-4, 16px)",
              "& .MuiAlert-icon": { color: "var(--sem-error, #a53229)" },
            }}
          >
            解約処理後、取り消すことはできません。
            {hasSmsList && (
              <>
                <br />
                解約時にメールまたはショートメッセージを送信する場合は下記から選択してください。
                <br />
                「送信しない」を選択した場合は解約のみ行い、送信は行いません。
              </>
            )}
          </Alert>
          {/* ★保全ガード③: SMS/メール送信選択・プレビュー機能枝は不可触（★不在§6-12=現行構造維持+トークン意匠のみ） */}
          {hasSmsList && (
            <>
              <Box sx={{ marginTop: "10px" }}>
                送信内容
              </Box>
              <FormControl
                fullWidth
                sx={{
                  marginTop: "10px",
                  marginBottom: "20px",
                  marginLeft: "5px",
                  width: "calc(100% - 10px)",
                }}
              >
                <Select
                  data-testid="data-cancel-select"
                  labelId="cancell-sms-select-label"
                  id="cancell-sms-select"
                  value={selectedValue}
                  onChange={(e) => {
                    const v = e.target.value as string;
                    if (v === SEND_NONE_VALUE) {
                      setCancelDataInf((prev) => ({
                        ...prev,
                        selectedCancellSms: null,
                      }));
                    } else {
                      const [idStr, source] = v.split(VALUE_SEP);
                      const id = parseInt(idStr, 10);
                      if (!isNaN(id) && (source === "product" || source === "outside_entry_form")) {
                        setCancelDataInf((prev) => ({
                          ...prev,
                          selectedCancellSms: { id, source },
                        }));
                      }
                    }
                  }}
                  sx={{
                    height: "30px",
                    textAlign: "end",
                    fontSize: "14px",
                  }}
                >
                  <MenuItem
                    value={SEND_NONE_VALUE}
                    sx={{ fontSize: "14px", height: "24px", minHeight: "auto" }}
                  >
                    送信しない
                  </MenuItem>
                  {cancelDataInf.cancellSmsList.map((message, idx) => (
                    <MenuItem
                      key={`${message.id}-${message.source}-${idx}`}
                      value={`${message.id}${VALUE_SEP}${message.source}`}
                      sx={{ fontSize: "14px", height: "24px", minHeight: "auto" }}
                    >
                      {message.cancell_sms_name}
                      {message.cancell_sms_type === 0
                        ? "（ショートメッセージ）"
                        : "（メール）"}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {selectedItem && (
                <Box sx={{ mt: 1, mb: 2 }}>
                  {selectedItem.cancell_sms_type === 0 ? (
                    <>
                      <Box>送信元ID</Box>
                      <TextField
                        disabled
                        size="small"
                        value={selectedItem.cancell_sms_sender}
                        sx={{ width: "calc(100% - 10px)", margin: "4px 5px" }}
                      />
                      <Box>送信先電話番号</Box>
                      <TextField
                        disabled
                        size="small"
                        value={cancelDataInf.destinationPhoneNumber}
                        sx={{ width: "calc(100% - 10px)", margin: "4px 5px" }}
                      />
                      <Box>本文サンプル</Box>
                      <TextField
                        disabled
                        size="small"
                        value={selectedItem.cancell_sms_body}
                        multiline
                        rows={4}
                        sx={{ width: "calc(100% - 10px)", margin: "4px 5px" }}
                      />
                    </>
                  ) : (
                    <>
                      <Box>送信元メールアドレス</Box>
                      <TextField
                        disabled
                        size="small"
                        value={selectedItem.cancell_sms_sender}
                        sx={{ width: "calc(100% - 10px)", margin: "4px 5px" }}
                      />
                      <Box>送信先メールアドレス</Box>
                      <TextField
                        disabled
                        size="small"
                        value={cancelDataInf.destinationMailAddress}
                        sx={{ width: "calc(100% - 10px)", margin: "4px 5px" }}
                      />
                      <Box>タイトル</Box>
                      <TextField
                        disabled
                        size="small"
                        value={selectedItem.cancell_sms_title}
                        sx={{ width: "calc(100% - 10px)", margin: "4px 5px" }}
                      />
                      <Box>本文</Box>
                      <TextField
                        disabled
                        size="small"
                        value={selectedItem.cancell_sms_body}
                        multiline
                        rows={4}
                        sx={{ width: "calc(100% - 10px)", margin: "4px 5px" }}
                      />
                    </>
                  )}
                </Box>
              )}
            </>
          )}
        </>
      ) : cancelDataInf.status === "success" ? (
        <Box className="result-state -success" data-testid="result-state-success">
          <span className="rs-title">解約に成功しました。</span>
        </Box>
      ) : cancelDataInf.status === "failed" ? (
        <Box className="result-state -failed" data-testid="result-state-failed">
          <span className="rs-title">{cancelDataInf.message}</span>
        </Box>
      ) : null}
    </PoModalShell>
  );
}
